// Sugalarm — hands-free voice command session (cycle 383). ANDROID ONLY.
//
// One implementation, two surfaces: the wall-mount tablet `kiosk` APK and
// the `standard` Android phone app. On the iOS shell, on iPad, and in a
// plain browser this hook is a PERMANENT NO-OP — the native `VoiceCapture`
// plugin is not on the bridge there, so `enabled` is false, no listener is
// registered, no fetch is issued, and no voice code path executes.
//
// ─── SAFETY RULES (each pinned by a test where testable) ───────────────
//
//  1. VOICE IS FULLY DEFERRED WHILE AN ALARM IS SOUNDING. A session cannot
//     START during a live alarm, and an alarm firing MID-SESSION cancels it
//     immediately: recognition stopped, no partial intent dispatched, any
//     in-flight spoken answer cut. The alarm path is not modified in any
//     way to achieve this — this layer READS `alarmLive` and never writes
//     alarm state.
//  2. VOICE CAN NEVER SILENCE, SNOOZE, STOP OR ACKNOWLEDGE AN ALARM. That
//     is structural: `voiceGrammar` cannot express such an intent, so there
//     is nothing here to dispatch. See the tripwire test on the enum.
//  3. ECHO GUARD. We never listen while our own TTS is speaking, so the
//     device cannot recognize its own confirm-back as a command.
//  4. Nothing here reads billing or entitlements.
//
// ─── DEV GATE (SCAFFOLDING — REPLACED BY THE WAKE WORD NEXT CYCLE) ─────
// The whole feature is inert unless `localStorage.sugalarm_voice_dev` is
// "1" on the device. With the flag unset there is no trigger, no listener,
// no permission prompt, and every screen is byte-identical to cycle 382.
// The long-press is a bench affordance only; "Hey Sugalarm" replaces it.

import { useCallback, useEffect, useRef, useState } from "react";

import * as treatmentsApi from "../api/treatments";
import { isNativeKioskShell } from "../lib/capacitor";
import {
  composeGlucoseUtterance,
  resolveUtteranceContext,
} from "../lib/utterance";
import {
  enqueue,
  loadQueue,
  queueCount,
  removePending,
  treatmentMatchesPending,
} from "../lib/treatmentQueue";
import type { VoiceIntent } from "../lib/voiceGrammar";
import { parseTranscript } from "../lib/voiceGrammar";
import * as voiceCapture from "../lib/voiceCapture";
import * as wakeWord from "../lib/wakeWord";
import { canOpenMic, canSpeak, shouldCancelForAlarm } from "../lib/voiceSession";
import type { VoiceErrorCode } from "../lib/voiceCapture";
import {
  CANCELLED_TEXT,
  MISS_TEXT,
  confirmBackText,
  getIOBText,
  getInsulinTimeLeftText,
  getLastDoseText,
  loggedText,
} from "../lib/voiceIntents";
import type { ApiErrorKind, Treatment, TreatmentCreateIn } from "../lib/types";
import type { FrameVariant } from "./useFrameVariant";
import type { GlucoseSocketState } from "./useGlucoseSocket";
import type { TTSState } from "./useTTS";

/** localStorage key that arms the whole feature. Scaffolding — see header. */
export const VOICE_DEV_FLAG_KEY = "sugalarm_voice_dev";

/** Long-press duration on the hidden trigger. Deliberately long: this must
 *  never fire from an ordinary tap on the kiosk clock or the mobile header
 *  avatar, both of which are live UI in their own right. */
export const LONG_PRESS_MS = 3000;

/** Movement past this cancels the press, so a scroll or a drag can't arm a
 *  listening session. */
const LONG_PRESS_SLOP_PX = 12;

/** How long we wait for the yes/no after a `logInsulin` confirm-back.
 *  Silence = no (cycle-101 semantics). */
export const CONFIRM_WINDOW_MS = 5000;

/** If TTS never actually starts within this window (a total failure of
 *  both the server voice and the local fallback), release the speech latch
 *  so a session can never wedge waiting for audio that will not arrive. */
const SPEECH_START_TIMEOUT_MS = 8000;

export type VoiceSessionPhase = "idle" | "listening" | "processing" | "speaking";

/** Which Android build we're on. Drives the permission policy split only. */
type Surface = "kiosk" | "phone";

/** True iff the device has been armed for voice-command development. */
export function isVoiceDevEnabled(): boolean {
  try {
    return localStorage.getItem(VOICE_DEV_FLAG_KEY) === "1";
  } catch {
    // Private mode / storage disabled → treat as unset (inert).
    return false;
  }
}

export interface UseVoiceCommandsOpts {
  variant: FrameVariant;
  tts: TTSState;
  socket: GlucoseSocketState;
  activeMemberId: string | null;
  memberName: string | null;
  /** `episode.isLive` — an alarm episode is live (sounding, snoozed, or in
   *  its recovery dwell). READ ONLY; this hook never writes alarm state. */
  alarmLive: boolean;
}

export interface VoiceCommandsState {
  /** Drives the listening banner. True while capturing or thinking. */
  listening: boolean;
  phase: VoiceSessionPhase;
  /** Treatment writes waiting to sync (the SHARED cycle-101 persistent
   *  queue — one localStorage store, reused rather than duplicated).
   *
   *  `null` when this hook is disabled, which is what lets the Now screen's
   *  existing "N pending sync" hint pick a single authority: prefer this
   *  count when the voice layer is live, otherwise fall back to the
   *  cycle-101 dispatcher's. Returning 0 while disabled would let a stale
   *  reading win a `Math.max`, and a lingering "1 pending sync" when
   *  nothing is pending is a lie on a medical display. */
  pendingCount: number | null;
}

function isTransient(kind: ApiErrorKind): boolean {
  return (
    kind === "network" || kind === "server" || kind === "rate_limit" || kind === "unknown"
  );
}

function mergeTreatments(
  rest: Treatment[],
  live: Treatment[] | undefined,
  deleted: Set<string> | undefined,
): Treatment[] {
  const byId = new Map<string, Treatment>();
  for (const t of rest) byId.set(t.id, t);
  if (live) for (const t of live) byId.set(t.id, t);
  if (deleted) for (const id of deleted) byId.delete(id);
  return [...byId.values()];
}

export function useVoiceCommands(
  opts: UseVoiceCommandsOpts,
): VoiceCommandsState {
  const { variant, tts, socket, activeMemberId, memberName, alarmLive } = opts;

  // ─── surface + enablement ────────────────────────────────────────────
  // `isNativeKioskShell()` is the authoritative "this is the kiosk APK"
  // test (cycle 267 — it detects kiosk-flavor-only plugins). Everything
  // else that reaches here is the Android phone app, because the
  // VoiceCapture plugin only exists in the two Android builds.
  const [pluginReady, setPluginReady] = useState(false);
  const devEnabled = isVoiceDevEnabled();
  const surface: Surface | null = isNativeKioskShell()
    ? "kiosk"
    : variant === "mobile" && voiceCapture.isPluginPresent()
      ? "phone"
      : null;
  const enabled = devEnabled && surface !== null && pluginReady;

  // Probe the recognizer once. Gated on the dev flag so a production
  // device makes ZERO plugin calls.
  useEffect(() => {
    if (!devEnabled || surface == null) {
      setPluginReady(false);
      return;
    }
    let cancelled = false;
    void voiceCapture.isAvailable().then((ok) => {
      if (!cancelled) setPluginReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [devEnabled, surface]);

  const [phase, setPhase] = useState<VoiceSessionPhase>("idle");
  const [pendingCount, setPendingCount] = useState<number>(0);

  // ─── refs so async callbacks read current values, never stale ────────
  const phaseRef = useRef<VoiceSessionPhase>("idle");
  const setPhaseBoth = useCallback((p: VoiceSessionPhase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  /** Non-null while a confirm-back is armed and awaiting a yes/no. */
  const pendingDoseRef = useRef<number | null>(null);
  const confirmTimerRef = useRef<number | null>(null);
  /** The session we intend to start once TTS finishes (echo guard). */
  const queuedStartRef = useRef<"command" | "confirm" | null>(null);

  // The PRIMITIVE boolean, extracted once: the speech-ended effect below
  // must depend on this and nothing else. `tts` itself is a fresh object
  // every render, so depending on it would make that effect run per-render
  // — which is exactly how the echo guard was defeated in review.
  const ttsSpeaking = tts.speaking;
  const ttsSpeakingRef = useRef(ttsSpeaking);
  ttsSpeakingRef.current = ttsSpeaking;

  const alarmLiveRef = useRef(alarmLive);
  alarmLiveRef.current = alarmLive;
  const activeMemberIdRef = useRef(activeMemberId);
  activeMemberIdRef.current = activeMemberId;
  const memberNameRef = useRef(memberName);
  memberNameRef.current = memberName;
  const latestReadingRef = useRef(socket.lastReading);
  latestReadingRef.current = socket.lastReading;
  const lastAlertRef = useRef(socket.lastAlert);
  lastAlertRef.current = socket.lastAlert;

  const devLog = useCallback(
    (...args: unknown[]) => {
      // Client-side debug only, behind the same dev flag. Never a
      // transcript, and never anything that leaves the device.
      if (devEnabled) console.debug("[voice]", ...args);
    },
    [devEnabled],
  );

  // ─── treatments for answering (REST seed merged with the live cache) ──
  // The ANSWER itself is computed from this in-memory array, so every query
  // works with the server unreachable; the seed is best-effort only.
  const [restTreatments, setRestTreatments] = useState<Treatment[]>([]);
  const refetch = useCallback(async () => {
    const r = await treatmentsApi.list(4, 50);
    if (r.ok) setRestTreatments(r.data.treatments);
  }, []);
  useEffect(() => {
    if (enabled) void refetch();
  }, [enabled, refetch, activeMemberId]);

  const liveCache = activeMemberId
    ? socket.byMember[activeMemberId]?.recentTreatments
    : undefined;
  const deletedIds = activeMemberId
    ? socket.byMember[activeMemberId]?.deletedTreatmentIds
    : undefined;
  const treatmentsRef = useRef<Treatment[]>([]);
  treatmentsRef.current = mergeTreatments(restTreatments, liveCache, deletedIds);

  useEffect(() => {
    if (enabled) setPendingCount(queueCount());
  }, [enabled]);

  // ─── speaking ────────────────────────────────────────────────────────

  // `tts.speaking` lags `speak()` by a whole network round-trip and
  // `useTTS()` returns a fresh object every render, so NEITHER can be
  // sampled to decide "our speech is over". We keep our own latch and act
  // only on an observed true → false TRANSITION. See `lib/voiceSession.ts`
  // for the two bugs this shape exists to prevent.
  const ourSpeechRef = useRef(false);
  const sawSpeakingRef = useRef(false);
  const speechStartTimerRef = useRef<number | null>(null);

  const clearSpeechStartTimer = useCallback(() => {
    if (speechStartTimerRef.current != null) {
      window.clearTimeout(speechStartTimerRef.current);
      speechStartTimerRef.current = null;
    }
  }, []);

  const clearConfirmTimer = useCallback(() => {
    if (confirmTimerRef.current != null) {
      window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  }, []);

  /** End the session cleanly. `hard` also cuts our in-flight speech —
   *  used by the alarm cancel path, never by ordinary completion. */
  const endSession = useCallback(
    (hard: boolean) => {
      clearConfirmTimer();
      clearSpeechStartTimer();
      pendingDoseRef.current = null;
      queuedStartRef.current = null;
      void voiceCapture.stopListening();
      if (hard) {
        // Only ever cuts OUR utterance — `ourSpeechRef` is set by `speak()`.
        if (ourSpeechRef.current) tts.stop();
        ourSpeechRef.current = false;
        sawSpeakingRef.current = false;
      }
      setPhaseBoth("idle");
    },
    [clearConfirmTimer, clearSpeechStartTimer, tts, setPhaseBoth],
  );

  const beginListening = useCallback(async () => {
    // Re-check at the last moment — an alarm may have fired between the
    // trigger and this call, and our own speech must be finished.
    if (
      !canOpenMic({
        alarmLive: alarmLiveRef.current,
        ourSpeechInFlight: ourSpeechRef.current,
        ttsSpeaking: ttsSpeakingRef.current,
      })
    ) {
      devLog("mic refused: alarm live or our speech still in flight");
      return;
    }
    // Ask the wake service to release the microphone FIRST. Idempotent,
    // and a no-op when the service is stopped — but without it, a session
    // that did not come from a wake contends with the service for the mic.
    await wakeWord.yieldMic();
    setPhaseBoth("listening");
    await voiceCapture.startListening();
  }, [devLog, setPhaseBoth]);

  /** Runs exactly once per utterance, on the speech-ended transition (or
   *  on the start-timeout fallback). Owns the queued-start handoff. */
  const finishSpeech = useCallback(() => {
    clearSpeechStartTimer();
    ourSpeechRef.current = false;
    sawSpeakingRef.current = false;

    const queued = queuedStartRef.current;
    queuedStartRef.current = null;

    if (alarmLiveRef.current) {
      endSession(false);
      return;
    }
    if (queued == null) {
      if (phaseRef.current === "speaking") setPhaseBoth("idle");
      return;
    }
    if (queued === "confirm") {
      clearConfirmTimer();
      confirmTimerRef.current = window.setTimeout(() => {
        // Silence = no. Nothing is written.
        if (pendingDoseRef.current != null) {
          pendingDoseRef.current = null;
          void voiceCapture.stopListening();
          speakRef.current(CANCELLED_TEXT);
        }
      }, CONFIRM_WINDOW_MS);
    }
    void beginListening();
  }, [beginListening, clearConfirmTimer, clearSpeechStartTimer, endSession, setPhaseBoth]);

  const speak = useCallback(
    (text: string) => {
      // AN ALARM OUTRANKS EVERY SPOKEN ANSWER — refused at the source.
      // The alarm-cancel effect latches once per alarm, so by the time a
      // LATE utterance arrives (an awaited treatment POST resolving after
      // the alarm fired, a late recognizer error) there is nothing left to
      // cut it. Guarding here covers every call site at once, and means
      // this invariant no longer depends on `endSession`'s identity
      // churning each render — so memoizing `tts` in a future cycle cannot
      // silently break it.
      if (!canSpeak({ alarmLive: alarmLiveRef.current })) {
        devLog("speech refused: an alarm is live");
        queuedStartRef.current = null;
        pendingDoseRef.current = null;
        setPhaseBoth("idle");
        return;
      }
      setPhaseBoth("speaking");
      // Latch synchronously — BEFORE tts.speaking can possibly be true.
      ourSpeechRef.current = true;
      sawSpeakingRef.current = false;
      clearSpeechStartTimer();
      speechStartTimerRef.current = window.setTimeout(() => {
        // Audio never started at all — release rather than wedge.
        if (ourSpeechRef.current && !sawSpeakingRef.current) {
          devLog("speech never started — releasing the latch");
          finishSpeechRef.current();
        }
      }, SPEECH_START_TIMEOUT_MS);
      tts.speak(text);
    },
    [tts, setPhaseBoth, clearSpeechStartTimer, devLog],
  );

  // Ref-stable handles so the transition effect below depends ONLY on the
  // `tts.speaking` boolean — never on churning callback identities.
  const speakRef = useRef(speak);
  speakRef.current = speak;
  const finishSpeechRef = useRef(finishSpeech);
  finishSpeechRef.current = finishSpeech;

  // THE speech-ended edge. Depends on the boolean alone, so it runs only
  // when playback genuinely starts or stops.
  useEffect(() => {
    if (ttsSpeaking) {
      if (ourSpeechRef.current) sawSpeakingRef.current = true;
      return;
    }
    if (!ourSpeechRef.current) return;
    // Asked to speak, but audio has not begun yet — wait for it.
    if (!sawSpeakingRef.current) return;
    finishSpeechRef.current();
  }, [ttsSpeaking]);

  /** Request listening, deferring until our own speech finishes (echo
   *  guard). Never opens the mic over the app's own voice. */
  const requestListening = useCallback(
    (mode: "command" | "confirm") => {
      if (alarmLiveRef.current) return;
      if (
        !canOpenMic({
          alarmLive: alarmLiveRef.current,
          ourSpeechInFlight: ourSpeechRef.current,
          ttsSpeaking: ttsSpeakingRef.current,
        })
      ) {
        queuedStartRef.current = mode;
        return;
      }
      void beginListening();
    },
    [beginListening],
  );

  // ─── the dev-gated trigger (long-press on a marked element) ──────────

  const onTrigger = useCallback(() => {
    if (alarmLiveRef.current) {
      devLog("trigger ignored: alarm live");
      return;
    }
    if (phaseRef.current !== "idle") {
      devLog("trigger ignored: session already running");
      return;
    }
    void (async () => {
      // Phone: request the mic lazily on the first attempt. Kiosk: NEVER
      // prompt — the grant is pre-applied at provisioning, and a missing
      // one degrades to inert.
      const granted = await voiceCapture.checkPermission();
      if (!granted) {
        if (surface === "phone") {
          const ok = await voiceCapture.requestPermission();
          if (!ok) {
            // Denial degrades to inert. No nagging, no re-prompt loop —
            // we only ask again on the NEXT explicit trigger.
            devLog("mic permission denied — inert");
            return;
          }
        } else {
          devLog("mic permission absent on kiosk — inert (never prompting)");
          return;
        }
      }
      requestListening("command");
    })();
  }, [devLog, requestListening, surface]);

  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (!enabled) return;
    let timer: number | null = null;
    let startX = 0;
    let startY = 0;

    const clear = () => {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.("[data-voice-trigger]") == null) return;
      startX = e.clientX;
      startY = e.clientY;
      clear();
      timer = window.setTimeout(() => {
        timer = null;
        onTriggerRef.current();
      }, LONG_PRESS_MS);
    };
    const onMove = (e: PointerEvent) => {
      if (timer == null) return;
      if (
        Math.abs(e.clientX - startX) > LONG_PRESS_SLOP_PX ||
        Math.abs(e.clientY - startY) > LONG_PRESS_SLOP_PX
      ) {
        clear();
      }
    };

    // Capture phase + passive: we only ever OBSERVE. Nothing here calls
    // preventDefault or stopPropagation, so the clock's and the avatar's
    // ordinary behaviour is untouched.
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", clear, true);
    document.addEventListener("pointercancel", clear, true);
    return () => {
      clear();
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", clear, true);
      document.removeEventListener("pointercancel", clear, true);
    };
  }, [enabled]);

  // ─── the write path (optimistic insert + POST + offline queue) ───────

  const performLogInsulin = useCallback(
    async (units: number) => {
      const memberId = activeMemberIdRef.current;
      if (!memberId) {
        speak(MISS_TEXT);
        return;
      }
      const iso = new Date().toISOString();
      const payload: TreatmentCreateIn = {
        event_type: "insulin",
        timestamp: iso,
        insulin_u: units,
        provenance: { source: "voice" },
      };
      const clientId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `voice-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      const optimistic: Treatment = {
        id: clientId,
        member_id: memberId,
        user_id: "",
        event_type: "insulin",
        timestamp: iso,
        carbs_g: null,
        insulin_u: units,
        notes: null,
        entered_by: "voice",
        provenance: { source: "voice" },
        created_at: iso,
      };
      // Optimistic first: the IOB ring + countdown move immediately, before
      // any network round-trip.
      socket.injectTreatment(memberId, optimistic);

      const res = await treatmentsApi.create(payload);
      if (res.ok) {
        socket.forgetTreatment(memberId, clientId);
        socket.injectTreatment(memberId, res.data);
        speak(loggedText(units, false));
        void refetch();
      } else if (isTransient(res.error.kind)) {
        // Offline / transient — queue it and KEEP the optimistic row, so
        // the household sees the dose on board right away.
        enqueue({ clientId, payload, createdAt: Date.now() });
        setPendingCount(queueCount());
        speak(loggedText(units, true));
      } else {
        socket.forgetTreatment(memberId, clientId);
        speak("Sorry, I couldn't log that.");
      }
    },
    [socket, speak, refetch],
  );

  // ─── offline queue flush (reuses the cycle-101 persistent queue) ─────

  const flushInFlightRef = useRef(false);
  const flush = useCallback(async () => {
    if (!enabled || flushInFlightRef.current) return;
    const list = loadQueue();
    if (list.length === 0) {
      setPendingCount(0);
      return;
    }
    flushInFlightRef.current = true;
    const memberId = activeMemberIdRef.current;
    try {
      for (const p of list) {
        // Dedupe: did this write actually land before we lost the response?
        // If a real server row matches, drop it rather than POST twice.
        const landed = treatmentsRef.current.some((t) =>
          treatmentMatchesPending(t, p),
        );
        if (landed) {
          if (memberId) socket.forgetTreatment(memberId, p.clientId);
          removePending(p.clientId);
          continue;
        }
        const res = await treatmentsApi.create(p.payload);
        if (res.ok) {
          if (memberId) {
            socket.forgetTreatment(memberId, p.clientId);
            socket.injectTreatment(memberId, res.data);
          }
          removePending(p.clientId);
        } else if (isTransient(res.error.kind)) {
          break; // still offline — retry on the next signal
        } else {
          if (memberId) socket.forgetTreatment(memberId, p.clientId);
          removePending(p.clientId); // permanent failure — drop
        }
      }
    } finally {
      flushInFlightRef.current = false;
      setPendingCount(queueCount());
      void refetch();
    }
  }, [enabled, socket, refetch]);

  useEffect(() => {
    if (enabled) void flush();
  }, [enabled, flush]);
  useEffect(() => {
    if (enabled && socket.status === "open") void flush();
  }, [enabled, socket.status, flush]);
  useEffect(() => {
    if (!enabled) return;
    const onOnline = () => void flush();
    const onVis = () => {
      if (document.visibilityState === "visible") void flush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, flush]);

  // ─── intent dispatch ─────────────────────────────────────────────────

  const dispatchIntent = useCallback(
    (parsed: VoiceIntent) => {
      const now = Date.now();
      const awaitingConfirm = pendingDoseRef.current != null;

      if (awaitingConfirm) {
        const units = pendingDoseRef.current!;
        clearConfirmTimer();
        if (parsed.intent === "confirmYes") {
          pendingDoseRef.current = null;
          void performLogInsulin(units);
        } else if (parsed.intent === "confirmNo") {
          pendingDoseRef.current = null;
          speak(CANCELLED_TEXT);
        } else {
          // Anything else inside the confirm window is not a yes — do not
          // write. Fail closed.
          pendingDoseRef.current = null;
          speak(CANCELLED_TEXT);
        }
        return;
      }

      switch (parsed.intent) {
        case "getGlucose": {
          // The spoken zone comes from the reading's SERVER-STAMPED
          // classification via the cycle-309 utterance pipeline — never
          // from a client-side threshold comparison.
          const ctx = resolveUtteranceContext({
            reading: latestReadingRef.current,
            memberId: activeMemberIdRef.current,
            memberName: memberNameRef.current,
            lastAlert: lastAlertRef.current,
            now,
          });
          const r = composeGlucoseUtterance(ctx, {
            surface: "voice_query",
            staleMode: "speak",
          });
          if (r.text) speak(r.text);
          else setPhaseBoth("idle");
          break;
        }
        case "getIOB":
          speak(getIOBText(treatmentsRef.current, now));
          break;
        case "getInsulinTimeLeft":
          speak(getInsulinTimeLeftText(treatmentsRef.current, now));
          break;
        case "getLastDose":
          speak(getLastDoseText(treatmentsRef.current, now));
          break;
        case "logInsulin": {
          const units = Number(parsed.slots.units);
          if (!Number.isFinite(units)) {
            speak(MISS_TEXT);
            break;
          }
          // Speak the confirm-back, then open ONE confirm window. The echo
          // guard holds listening until this sentence finishes.
          pendingDoseRef.current = units;
          speak(confirmBackText(units));
          queuedStartRef.current = "confirm";
          break;
        }
        // confirmYes / confirmNo outside a confirm window are ignored by
        // design — they mean nothing on their own.
        default:
          setPhaseBoth("idle");
          break;
      }
    },
    [clearConfirmTimer, performLogInsulin, speak, setPhaseBoth],
  );

  // ─── native events ───────────────────────────────────────────────────

  const onFinal = useCallback(
    (transcript: string) => {
      if (alarmLiveRef.current) {
        // An alarm fired while the recognizer was finishing. Discard.
        endSession(true);
        return;
      }
      // A transcript that arrives after the session ended (cancel, alarm,
      // teardown) belongs to a session the user believes is over —
      // `recognizer.cancel()` should suppress it, but we do not depend on
      // that. Only a session we are actually listening in may dispatch.
      if (phaseRef.current !== "listening") {
        devLog("late transcript ignored (phase", phaseRef.current + ")");
        return;
      }
      setPhaseBoth("processing");
      const parsed = parseTranscript(transcript);
      devLog("final →", parsed?.intent ?? "null");
      if (parsed == null) {
        if (pendingDoseRef.current != null) {
          // An unparseable answer inside the confirm window is not a yes.
          clearConfirmTimer();
          pendingDoseRef.current = null;
          speak(CANCELLED_TEXT);
        } else {
          speak(MISS_TEXT);
        }
        return;
      }
      dispatchIntent(parsed);
    },
    [clearConfirmTimer, devLog, dispatchIntent, endSession, speak, setPhaseBoth],
  );

  const onError = useCallback(
    (code: VoiceErrorCode) => {
      devLog("error", code);
      // Mirrors the late-transcript guard: an error belonging to a session
      // we already ended must not speak. Without this, the confirm-window
      // timeout (which stops the recognizer and then says "cancelled")
      // could be followed by a `no_match` from that very stop, producing a
      // second, contradictory utterance.
      if (phaseRef.current !== "listening" && pendingDoseRef.current == null) {
        devLog("late error ignored (phase", phaseRef.current + ")");
        return;
      }
      if (pendingDoseRef.current != null) {
        // Any failure inside the confirm window means no confirmation was
        // heard → nothing is written.
        clearConfirmTimer();
        pendingDoseRef.current = null;
        speak(CANCELLED_TEXT);
        return;
      }
      if (code === "no_match") {
        speak(MISS_TEXT);
        return;
      }
      // timeout / audio / permission / recognizer_unavailable / busy all
      // degrade silently to idle — nobody said anything worth answering.
      endSession(false);
    },
    [clearConfirmTimer, devLog, endSession, speak],
  );

  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled) return;
    return voiceCapture.subscribe({
      onFinal: (t) => onFinalRef.current(t),
      onError: (c) => onErrorRef.current(c),
      // Partials are deliberately NOT dispatched — only a final transcript
      // can produce an intent, so a half-heard sentence can never act.
    });
  }, [enabled]);

  // ─── wake word (cycle 385) ───────────────────────────────────────────
  //
  // THE SPA'S HALF OF THE MICROPHONE HANDOFF. The service releases the mic
  // BEFORE announcing a detection and then waits; `resume()` gives it back.

  /** A wake fired. Same session the long-press opens — minus the permission
   *  request, which is moot: the service could not have been listening
   *  without the grant. */
  const onWake = useCallback(() => {
    // ⚠ A wake is DROPPED, never queued. Review found that routing it
    // through the queueing path let an accepted wake sit behind somebody
    // else's utterance (auto-announce, a reminder — the TTS instance is
    // shared): the phase never left `idle`, so the resume effect never
    // fired, the microphone was neither used nor returned, and the queued
    // start persisted to open the mic at the end of some later, unrelated
    // session. On a kiosk that auto-announces on a cadence that is routine,
    // not a corner. A missed wake costs one repeat; a deferred one is a
    // microphone opening at a moment nobody asked for.
    if (
      alarmLiveRef.current ||
      phaseRef.current !== "idle" ||
      ourSpeechRef.current ||
      ttsSpeakingRef.current
    ) {
      // Refused. Hand the microphone straight back rather than leaving the
      // service yielded until its self-heal timeout — that window is a
      // device that is not listening.
      devLog("wake ignored (alarm live or session running)");
      void wakeWord.resume();
      return;
    }
    requestListening("command");
  }, [devLog, requestListening]);

  const onWakeRef = useRef(onWake);
  onWakeRef.current = onWake;

  useEffect(() => {
    if (!enabled) return;
    return wakeWord.subscribe({ onDetected: () => onWakeRef.current() });
  }, [enabled]);

  // Return the microphone on EVERY session-end path. Keying on the
  // transition to `idle` is what makes that exhaustive — success, miss,
  // recogniser error, confirm timeout, alarm cancel and teardown all pass
  // through it, so there is no path to forget. `resume()` is idempotent and
  // the service ignores one it did not yield for.
  const prevPhaseRef = useRef<VoiceSessionPhase>("idle");
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (prev !== "idle" && phase === "idle") void wakeWord.resume();
  }, [phase]);

  // ─── alarm cancellation (safety rule 1) ──────────────────────────────

  // Fires on the alarm's RISING EDGE and does not consult `phase` on its
  // own — review caught that phase bookkeeping could read "idle" while an
  // answer was still being spoken, which made this early-return and let
  // the answer talk over a sounding alarm. `ourSpeechInFlight` is the term
  // that closes that hole; the decision itself is the unit-tested
  // `shouldCancelForAlarm`.
  const alarmCancelledRef = useRef(false);
  useEffect(() => {
    if (!alarmLive) {
      alarmCancelledRef.current = false;
      return;
    }
    if (alarmCancelledRef.current) return;
    if (
      !shouldCancelForAlarm({
        alarmLive: true,
        sessionActive: phaseRef.current !== "idle",
        hasPendingDose: pendingDoseRef.current != null,
        ourSpeechInFlight: ourSpeechRef.current,
      })
    ) {
      return;
    }
    alarmCancelledRef.current = true;
    devLog("alarm fired mid-session — cancelling");
    endSession(true);
  }, [alarmLive, devLog, endSession]);

  // Tear the session down if the feature is disabled or the hook unmounts,
  // so a flag flip or a sign-out can never leave the mic open.
  useEffect(() => {
    if (enabled) return;
    if (phaseRef.current !== "idle") endSession(true);
    // Stop the wake service too. Clearing the dev flag unmounts the only
    // stop control, which would otherwise leave a LISTENING service holding
    // the microphone with no way to reach it and nothing consuming its
    // detections.
    void wakeWord.stop();
  }, [enabled, endSession]);

  // Unmount (sign-out, shell teardown) must run the SAME teardown the
  // disable path runs — review caught that stopping only the recognizer
  // left the confirm (≤5 s) and speech-start (≤8 s) timers armed, so a
  // leaked timer could fire `speak()` against a torn-down TTS hook. Held
  // through a ref because `endSession` closes over `tts`, whose identity
  // churns every render; a `[]` cleanup would capture a stale closure.
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;
  useEffect(() => {
    return () => {
      endSessionRef.current(true);
      // Directly, NOT via the phase-transition effect: a state set during
      // unmount never re-renders, so that effect cannot fire. Review caught
      // this as a real hole in the "resume on every path" claim.
      void wakeWord.resume();
    };
  }, []);

  return {
    listening: phase === "listening" || phase === "processing",
    phase,
    pendingCount: enabled ? pendingCount : null,
  };
}
